import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import HolaVidaLogo from "src/assets/HolaVidaLogo";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useIsMobile } from "src/hooks/useIsMobile";
import { supabaseClient } from "src/lib/supabase/supabaseClient";

const UpdatePassword = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [password, setPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [displayAlert, setDisplayAlert] = useState<boolean>(false);
  const [passwordVisibility, setPasswordVisibility] = useState<boolean>(false);
  const { t } = useTranslation();
  const page = "update-password";
  const errorMessage = useMemo(() => {
    const hash = window.location.hash.substring(1);

    const params = new URLSearchParams(hash);

    return params.get("error_description");
  }, []);

  const onSuccess = useCallback(async () => {
    await supabaseClient.auth.signOut();
    navigate("/login");
  }, [navigate]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      try {
        const { data, error } = await supabaseClient.auth.updateUser({
          password,
        });

        if (error != null) {
          if (
            error.message.includes(
              "New password should be different from the old password."
            )
          ) {
            setError(t(`${page}.same-password-error`));
          } else {
            setError(error.message);
          }
        }

        if (data.user != null) {
          setLoading(false);
          setDisplayAlert(true);
          setPassword("");

          const navigateTimeout = setTimeout(async () => {
            await onSuccess();
          }, 1500);

          return () => clearTimeout(navigateTimeout);
        }
      } catch (err) {
        console.error("Unexpected error", err);
      } finally {
        setLoading(false);
      }
    },
    [onSuccess, password, t]
  );

  return errorMessage ? (
    <Typography variant="h4">{errorMessage}</Typography>
  ) : (
    <Stack
      alignItems="center"
      justifyContent="center"
      height="100%"
      padding="1rem"
    >
      <Stack gap="3rem">
        <Box
          component="form"
          role="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "max-contnet",
            maxWidth: "380px",
            width: isMobile ? "fit-content" : "100%",
            boxShadow: 10,
            p: isMobile ? 3 : 5.5,
            borderRadius: "12px",
            backgroundColor: "#ffffff",
          }}
        >
          <Box sx={{ alignSelf: "center" }}>
            <HolaVidaLogo
              style={{
                width: isMobile ? "145" : "180",
                height: isMobile ? "80" : "90",
              }}
            />
          </Box>
          <Stack sx={{ mt: 1 }} gap={3}>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight={700}
              width={isMobile ? "320px" : "400px"}
            >
              {t(`${page}.title`)}
            </Typography>
            <Stack gap={4}>
              <TextField
                type={passwordVisibility ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                id="filled-adornment-password"
                label={t(`login.password.label`)}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setPasswordVisibility((o) => !o)}
                          onMouseDown={() => setPasswordVisibility((o) => !o)}
                        >
                          {passwordVisibility ? (
                            <VisibilityOutlined />
                          ) : (
                            <VisibilityOffOutlined />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {error ? (
                <Typography sx={{ color: "red" }} textAlign="center">
                  {error}
                </Typography>
              ) : null}
              <LoadingButton
                loading={loading}
                type="submit"
                disabled={displayAlert}
              >
                {t(`${page}.submit`)}
              </LoadingButton>

              {displayAlert ? (
                <Alert severity="success">{t(`${page}.success-alert`)}</Alert>
              ) : null}
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};

export default UpdatePassword;
